import differenceInHours from "date-fns/differenceInHours";
import zonedTimeToUtc from "date-fns-tz/zonedTimeToUtc";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { ContentPlaceholder } from "~/components/content/content-placeholder";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { config } from "~/data";
import { staticPathname as storyStylePageStaticPathname } from "~/pages/story-style";
import type { storyCardCarouselItemArticle$key } from "~/queries/__generated__/storyCardCarouselItemArticle.graphql";

import {
  RedDotIcon,
  StoryCardCarouselItemContainer,
  StyleCardItemSectionName,
  StyledBaseImage,
  StyledBaseLink,
} from "./styles";

type Props = {
  pathname: string;
  reference: storyCardCarouselItemArticle$key;
  sectionName: string;
};

export const StoryCardCarouselItem: FunctionComponent<Props> = ({
  pathname,
  reference: reference_,
  sectionName,
}) => {
  const article = useFragment(
    graphql`
      fragment storyCardCarouselItemArticle on Article {
        publishedDate
        images(filter: { type: { exclude: null } }) {
          alt
          isSlideshow
          resized: style(filter: { style: "134w_portrait" }) {
            url
          }
        }
        ...entityLink
      }
    `,
    reference_,
  );

  const styleStoryCardPathname = `${storyStylePageStaticPathname}/${pathname}`;

  const handleRenderPublishWithin24HoursRedDot = () => {
    if (!article?.publishedDate) return null;

    const now = zonedTimeToUtc(new Date(), config.date.defaultTimeZone).getTime();
    const hours = differenceInHours(now, new Date(article.publishedDate));
    return hours <= 24 ? <RedDotIcon /> : null;
  };

  const handleRenderImage = () => {
    const filteredImage = (article?.images ?? []).find(image => image?.isSlideshow);
    const alt = filteredImage?.alt ?? "";
    const source = filteredImage?.resized?.url ?? "";

    if (!source.trim()) {
      return <ContentPlaceholder />;
    }

    return (
      <StyledBaseImage
        $aspectRatio="2 / 3"
        $height="auto"
        $objectFit="cover"
        $width="100%"
        alt={alt}
        src={source}
      />
    );
  };

  const handleClick = () => {
    const url = new URL(`${window.location.origin}${styleStoryCardPathname}`);
    if (!url) return;

    url.searchParams.set("origin", window.location.href);
    sendGATracking({
      action: `Story Card/Style/Carousel/Click`,
      category: "Story Card",
      label: url.toString(),
    });
  };

  return (
    <StoryCardCarouselItemContainer>
      <StyledBaseLink
        pathname={styleStoryCardPathname}
        query={{
          origin: window.location.href,
        }}
        onClick={handleClick}
      >
        {handleRenderImage()}
      </StyledBaseLink>
      {handleRenderPublishWithin24HoursRedDot()}
      <StyleCardItemSectionName>{sectionName}</StyleCardItemSectionName>
    </StoryCardCarouselItemContainer>
  );
};

StoryCardCarouselItem.displayName = "StoryCardCarouselItem";
